import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { SoftwareListItem } from '../api/types'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { Card, ErrorBanner, Spinner } from '../components/common'
import type { Column } from '../components/DataTable'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Inventario Software → Elenco dei software: every product installed in the
 * tenant (the software inventory of the CIs: package managers, Windows
 * programs, unpacked products, custom paths), by name and version, split into
 * system software and software outside the system; each row opens the hosts
 * that have it. See software_inventory_service.
 */

type Tipo = '' | 'system' | 'extra'

type DoveRiga = { ci_id: string; ci_name: string; version: string; path: string; source: string }

function DoveInstallato({ s }: { s: SoftwareListItem }) {
  const dove = useAsync(() => api.softwareWhere(s.name, s.version), [s.name, s.version])
  if (dove.loading && !dove.data) return <Spinner />
  const colonne: Column<DoveRiga>[] = [
    { key: 'ci_name', header: t('Host'), render: (d) => <Link to={`/cis/${d.ci_id}`}>{d.ci_name}</Link> },
    { key: 'version', header: t('Versione'), mono: true },
    { key: 'source', header: t('Origine') },
    { key: 'path', header: t('Percorso'), mono: true, render: (d) => <small>{d.path || '—'}</small> },
  ]
  return (
    <div className="card__inset">
      <ErrorBanner error={dove.error} />
      <DataTable rows={dove.data ?? []} columns={colonne} rowKey={(d, i) => `${d.ci_id}:${d.path}:${i}`}
        conteggio={{ nome: t('host') }} />
    </div>
  )
}

export default function SoftwareList() {
  const [tipo, setTipo] = useState<Tipo>('')
  const dati = useAsync(() => api.softwareList({ kind: tipo }), [tipo])
  const n = (x: number) => x.toLocaleString(localeCorrente())
  const colonne: Column<SoftwareListItem>[] = [
    { key: 'name', header: t('Software') },
    { key: 'version', header: t('Versione'), mono: true },
    { key: 'vendor', header: t('Produttore') },
    { key: 'kind', header: t('Tipo'), value: (s) => (s.kind === 'system' ? t('Di sistema') : t('Extra sistema')),
      render: (s) => <span className={`badge badge--${s.kind === 'system' ? 'muted' : 'info'}`}>{s.kind === 'system' ? t('Di sistema') : t('Extra sistema')}</span> },
    { key: 'source', header: t('Origine') },
    { key: 'hosts', header: t('Host'), value: (s) => s.hosts, filter: false },
  ]
  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Elenco dei software')}</h1>
          <p className="page__subtitle">
            {t('Inventario centralizzato dei software rilevati sui CI del tenant, identificati per nome e versione tramite package manager, programmi Windows, applicazioni distribuite e percorsi configurati. Per ogni prodotto mostra i CI su cui è installato, distinguendo tra software di sistema e software applicativo.')}
          </p>
        </div>
        <BottoniEsporta
          nome="censo-software"
          disabled={!dati.data}
          path={(fmt) => api.softwareListExportPath(fmt, { kind: tipo })}
        />
      </header>
      {dati.data && (
        <div className="stats">
          <button className={`stat${tipo === '' ? ' stat--ok' : ''}`} onClick={() => setTipo('')}>
            <div className="stat__value">{n(dati.data.counts.system + dati.data.counts.extra)}</div>
            <div className="stat__label">{t('Software installati (nome e versione)')}</div>
            <div className="stat__hint">{t('su {n} host', { n: n(dati.data.hosts) })}</div>
          </button>
          <button className="stat" onClick={() => setTipo('system')}>
            <div className="stat__value">{n(dati.data.counts.system)}</div>
            <div className="stat__label">{t('Software di sistema')}</div>
          </button>
          <button className="stat" onClick={() => setTipo('extra')}>
            <div className="stat__value">{n(dati.data.counts.extra)}</div>
            <div className="stat__label">{t('Software extra sistema')}</div>
          </button>
        </div>
      )}
      <Card>
        <div className="segmented" role="tablist" style={{ marginBottom: 8 }}>
          {([['', t('Tutti')], ['system', t('Di sistema')], ['extra', t('Extra sistema')]] as Array<[Tipo, string]>).map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tipo === k} className={tipo === k ? 'segmented__on' : ''} onClick={() => setTipo(k)}>{l}</button>
          ))}
        </div>
        <ErrorBanner error={dati.error} />
        {dati.loading && !dati.data ? <Spinner /> : (
          <DataTable rows={dati.data?.items ?? []} rowKey={(s) => `${s.name}@${s.version}@${s.vendor}@${s.source}`} columns={colonne}
            initialSort={{ key: 'hosts', dir: 'desc' }} conteggio={{ nome: t('software') }}
            paginazione={{ predefinita: 50, opzioni: [25, 50, 100, 250] }}
            expand={(s) => <DoveInstallato s={s} />} />
        )}
      </Card>
    </>
  )
}
